/* eslint-disable prettier/prettier */
import { useCallback, useEffect, useState } from 'react';
import { Theme, useMediaQuery } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'store';
import { useDialog, useOpenChatOnNewMessage, useSignalrConnection, useVideo } from 'hooks';
import { ISignalrConnectionContext } from 'context';
import { AINotesMenu, NotesDialog, PatientQueue } from 'components';
import { getRoomHistory } from 'store/call';
import { selectUser } from 'store/user';
import { ApiUser } from 'types';
import { parseIdentity } from 'utils';
import { EditWaitingRoomContainer } from './components';
import styles from './EditRoomPage.module.scss';
import { useTranslation } from 'react-i18next';

export function EditRoomPage() {
  // will be moved to Chat component
  useOpenChatOnNewMessage();
  const dispatch = useAppDispatch();
  const {
    signalrConnectionId,
    events,
    getPatientsList,
    addEventListener,
    removeEventListener,
  }: ISignalrConnectionContext = useSignalrConnection();
  const { showDialog } = useDialog();
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const { room } = useVideo();
  const user: ApiUser | null = useAppSelector(selectUser);
  const { t } = useTranslation('providerRoom');
  const [isEditWaitingRoomDirty, setIsEditWaitingRoomDirty] = useState(false);

  const confirmEditRoomNavigation = useCallback((): boolean => {
    if (!isEditWaitingRoomDirty) {
      return true;
    }

    if (!window.confirm(t('unsavedChangesPrompt'))) {
      return false;
    }

    setIsEditWaitingRoomDirty(false);
    return true;
  }, [isEditWaitingRoomDirty, t]);

  useEffect(() => {
    if (room) {
      return () => {
        room?.disconnect();
      };
    }
  }, [room]);

  useEffect(() => {
    if (signalrConnectionId) {
      getPatientsList();
    }
  }, [signalrConnectionId]);

  useEffect(() => {
    events.on('updatedStatusDataSent', getPatientsList);
  }, [events]);

  const onCallEnded = useCallback(() => {
    const participantNamesOverride = Array.from(room?.participants.values() ?? [])
      .map((participant) => {
        const [fallbackIdentity, displayName] = parseIdentity(participant.identity ?? '');
        return (displayName || fallbackIdentity || '').trim();
      })
      .filter(Boolean);

    dispatch(getRoomHistory()).then(() => {
      const modal = showDialog(NotesDialog, {
        participantNamesOverride,
        onConfirm: () => {
          modal.destroy();
        },
      });
    });
  }, [dispatch, room, showDialog]);

  useEffect(() => {
    addEventListener('callEnded', onCallEnded);

    return () => {
      removeEventListener('callEnded', onCallEnded);
    };
  }, [onCallEnded]);

  return (
    <div className={styles.container}>
      {isDesktop && (
        <PatientQueue
          className={styles.patientQueue}
          additionalMenuSlot={
            <AINotesMenu
              aiCreditsCount={user?.clinicUser.aiCreditsCount}
              confirmNavigation={confirmEditRoomNavigation}
            />
          }
        />
      )}
      <EditWaitingRoomContainer onDirtyChange={setIsEditWaitingRoomDirty} />
    </div>
  );
}
